import { useState, useEffect, useCallback, useRef } from 'react';
import { AuthState } from '../types';
import { ImsUserProfile } from '../types';
import {
  hasValidToken,
  getAccessToken,
  initiateLogin,
  handleOAuthCallback,
  hasOAuthCallback,
  fetchUserProfile,
  clearAuthData,
  federatedLogout,
} from '../services/auth';
import { AUTH_TOKEN_EXPIRED_EVENT, resetTokenExpiredFlag, fetchMyOrgs } from '../services/api';
import { IS_LOCAL } from '../config/config';

// Default stub profile used when running in local mode (no IMS).
const LOCAL_STUB_PROFILE: ImsUserProfile = {
  userId: 'local-user',
  email: 'local@localhost',
  displayName: 'Local Developer',
  first_name: 'Local',
  last_name: 'Developer',
  projectedProductContext: [
    {
      prodCtx: {
        owningEntity: 'local-org-id',
      },
    },
  ],
};

export function useAppAuth(): AuthState & { login: () => Promise<void>; logout: () => void } {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    userOrgIds: [],
    orgNames: {},
    error: null,
  });

  useEffect(() => {
    const initAuth = async () => {
      // ----- LOCAL environment: skip IMS entirely -----
      if (IS_LOCAL) {
        console.info('[Auth] Local environment detected — skipping IMS authentication');
        setAuthState({
          isAuthenticated: true,
          isLoading: false,
          user: LOCAL_STUB_PROFILE,
          userOrgIds: ['local-org-id'],
          orgNames: { 'local-org-id': 'Local Development' },
          error: null,
        });
        return;
      }

      // ----- Non-local environments: normal IMS OAuth flow -----
      try {
        if (hasOAuthCallback()) {
          try {
            await handleOAuthCallback();
          } catch (error) {
            setAuthState({
              isAuthenticated: false,
              isLoading: false,
              user: null,
              userOrgIds: [],
              orgNames: {},
              error: error instanceof Error ? error.message : 'OAuth callback failed',
            });
            return;
          }
        }

        if (hasValidToken()) {
          const accessToken = getAccessToken();
          if (accessToken) {
            let profile: ImsUserProfile;

            try {
              profile = await fetchUserProfile(accessToken);
            } catch (error) {
              console.error('Failed to fetch user profile:', error);
              clearAuthData();
              setAuthState({
                isAuthenticated: false,
                isLoading: false,
                user: null,
                userOrgIds: [],
                orgNames: {},
                error: 'Failed to fetch user profile. Please login again.',
              });
              return;
            }

            // Fetch org memberships from backend (handles IMS + personal-org fallback)
            let orgIds: string[] = [];
            let orgNames: Record<string, string> = {};
            try {
              const orgData = await fetchMyOrgs();
              orgIds = orgData.orgIds;
              orgNames = orgData.orgNames;
            } catch (err) {
              console.warn('Could not fetch organizations:', err);
            }

            setAuthState({
              isAuthenticated: true,
              isLoading: false,
              user: profile,
              userOrgIds: orgIds,
              orgNames,
              error: null,
            });
            return;
          }
        }

        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          userOrgIds: [],
          orgNames: {},
          error: null,
        });
      } catch (error) {
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          userOrgIds: [],
          orgNames: {},
          error: error instanceof Error ? error.message : 'Authentication error',
        });
      }
    };

    initAuth();
  }, []);

  const login = useCallback(async () => {
    // In local mode, just mark as authenticated (no IMS redirect)
    if (IS_LOCAL) {
      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        user: LOCAL_STUB_PROFILE,
        userOrgIds: ['local-org-id'],
        orgNames: { 'local-org-id': 'Local Development' },
        error: null,
      });
      return;
    }
    setAuthState((prev) => ({ ...prev, error: null }));
    await initiateLogin();
  }, []);

  const logout = useCallback(() => {
    if (IS_LOCAL) {
      clearAuthData();
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        userOrgIds: [],
        orgNames: {},
        error: null,
      });
    } else {
      federatedLogout();
    }
  }, []);

  // Auto-logout when the backend returns 401 (expired IMS token).
  // The API layer fires a custom DOM event; we listen here so React
  // state is cleaned up before the redirect.
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  useEffect(() => {
    const handleTokenExpired = () => {
      console.warn('[Auth] IMS token expired — triggering automatic logout');
      resetTokenExpiredFlag();
      logoutRef.current();
    };

    window.addEventListener(AUTH_TOKEN_EXPIRED_EVENT, handleTokenExpired);
    return () => {
      window.removeEventListener(AUTH_TOKEN_EXPIRED_EVENT, handleTokenExpired);
    };
  }, []);

  return {
    ...authState,
    login,
    logout,
  };
}
